import * as React from "react";
import { Button, Chip, CircularProgress, IconButton, MenuItem, Select, Table, TableBody, TableCell, TableFooter, TableHead, TablePagination, TableRow, TextField, Typography } from "@material-ui/core";
import { RaceTypeLabels } from "src/EnumsLabels";
import HighlightOff from '@material-ui/icons/Forward';
import { Race } from "src/api/model";
import NumberFormat from "react-number-format";
import AssignmentIcon from '@material-ui/icons/Assignment';
import { Link } from "react-router-dom";
import ClearIcon from '@material-ui/icons/Clear';
import CheckIcon from '@material-ui/icons/Check';
import UpdateIcon from '@material-ui/icons/Settings';

// Constantes fuera del componente para evitar recreación
const RACE_TYPES = [
    { id: 'SEND_PACKET', valor: 'Envio de paquetes' },
    { id: 'TAXI', valor: 'Taxi' },
    { id: 'DELIVERY', valor: 'Reparto' },
    { id: 'VENDOR', valor: 'Venta' },
    { id: 'REMISION', valor: 'Remision' },
    { id: 'OTHER', valor: 'Otros' },
    { id: 'RETORNO', valor: 'Retorno' }
];

const RACE_STATES = [
    { id: 'CREATED', valor: 'Creada' },
    { id: 'ASSIGNED', valor: 'Asignada' },
    { id: 'IN_PROGRESS', valor: 'En progreso' },
    { id: 'DELIVERED', valor: 'Entregada' },
    { id: 'CANCELLED', valor: 'Cancelada' }
];

const STATE_CHIP_STYLES: { [key: string]: React.CSSProperties } = {
    CREATED: { backgroundColor: '#1976d2', color: 'white' },
    ASSIGNED: { backgroundColor: '#0288d1', color: 'white' },
    IN_PROGRESS: { backgroundColor: '#03fce7', color: 'white' },
    DELIVERED: { backgroundColor: '#388e3c', color: 'white' },
    CANCELLED: { backgroundColor: '#d32f2f', color: 'white' },
    NOT_DELIVERED: { backgroundColor: '#fc7f03', color: 'white' }
};

const STATE_LABELS: { [key: string]: string } = {
    CREATED: 'Creada',
    ASSIGNED: 'Asignada',
    IN_PROGRESS: 'En progreso',
    DELIVERED: 'Entregada',
    CANCELLED: 'Cancelada',
    NOT_DELIVERED: 'No entregada'
};

// Función helper para renderizar chip de estado
const getStateChip = (state: string) => {
    const style = STATE_CHIP_STYLES[state] || { backgroundColor: 'grey', color: 'white' };
    const label = STATE_LABELS[state] || state;
    return <Chip style={style} label={label} />;
};

// Función helper para calcular distancia
const formatDistance = (routeLength: number) => {
    const km = parseFloat(((routeLength || 0) / 1000).toFixed(2));
    return (
        <NumberFormat 
            value={km}
            suffix={'Km.'}
            displayType={'text'}
            decimalScale={4}
        />
    );
};

export interface TableComponentProps {
    rutaMapa?: () => string;
    onFilterChange: (filter: string) => void;
    onPageNumChange: (pageNum: number) => void;
    onPageSizeChange: (pageSize: number) => void;
    onDateFromChange: (dateFrom: string) => void;
    onDateToChange: (dateTo: string) => void;
    onHandleTypeChange: (selectedType: string) => void;
    onHandleStateChange: (selectedState: string) => void;
    onHandleDriverChange: (driverId: number) => void;
    onHandleBranchChange: (branchId: number) => void;
    onHandleBranchDestinyChange: (branchId: number) => void;
    handleGeneralSearch: (search: string) => void;
    handleClearFilters: () => void;
    setDialog: (open: boolean, data: any) => void;
    setDialogBranch: (open: boolean, data: any) => void;
    setDialogDriver: (open: boolean, data: any) => void;
    setDialogClient: (open: boolean, data: any) => void;
    setDialogChangeState: (open: boolean, data: any) => void
    setSelection?: (data: any) => void;
    racetoAdd?: boolean;
    filter: string;
    data: Race[];
    dataDrivers: any[];
    dataBranches: any[];
    dataBranchesDestiny: any[];
    totalItems: number;
    pageNum: number;
    pageSize: number;
    dateFrom: string;
    dateTo: string;
    selectedType: string;
    selectedState: string;
    driverId: number;
    branchId: number;
    branchDestinyId: number;
    generalSearch: string;
    filtrosOn: boolean;
}

export const TableRacesComponent: React.Factory<TableComponentProps> = (propsArg) => {
    const props = propsArg as TableComponentProps;
    const dataDrivers = props.dataDrivers || [];
    const dataBranches = props.dataBranches || [];
    const dataBranchesDestiny = props.dataBranchesDestiny || [];
    const data = props.data;
    const racetoAdd = props.racetoAdd;
    const filtrosOn = props.filtrosOn;

    const handleDateFromChange = (e: React.FormEvent<HTMLInputElement>) => {
        props.onDateFromChange(e.currentTarget.value);
    };

    const handleDateToChange = (e: React.FormEvent<HTMLInputElement>) => {
        props.onDateToChange(e.currentTarget.value);
    };

    const handlePageChange = (event: any, newPage: number) => {
        props.onPageNumChange(newPage);
    };

    const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        props.onPageSizeChange(parseInt(event.target.value, 10));
    };

    const handleTypeChange = (event: React.ChangeEvent<{ value: unknown }>) => {
        props.onHandleTypeChange(event.target.value as string);
    };

    const handleStateChange = (event: React.ChangeEvent<{ value: unknown }>) => {
        props.onHandleStateChange(event.target.value as string);
    };

    const handleDriverChange = (event: React.ChangeEvent<{ value: unknown }>) => {
        props.onHandleDriverChange(event.target.value as number);
    };

    const handleBranchChange = (event: React.ChangeEvent<{ value: unknown }>) => {
        props.onHandleBranchChange(event.target.value as number);
    };

    const handleBranchDestinyChange = (event: React.ChangeEvent<{ value: unknown }>) => {
        props.onHandleBranchDestinyChange(event.target.value as number);
    };

    const handleGeneralSearch = (event: React.FormEvent<HTMLInputElement>) => {
        props.handleGeneralSearch(event.currentTarget.value);
    };

    const showNormalColumns = racetoAdd !== true;

    return (
        <>
            {filtrosOn && (
                <div style={{ display: 'flex' }}>
                    <div style={{ width: '100%' }}>
                        <TextField
                            name="search"
                            value={props.generalSearch}
                            id="searchName"
                            placeholder="Búsqueda por cliente"
                            onChange={handleGeneralSearch}
                            style={{ padding: 0 }}
                            autoFocus={true}
                        />
                    </div>
                    <div style={{ width: '100%' }}>
                        <TextField
                            label="Fecha Desde"
                            type="datetime-local"
                            value={props.dateFrom}
                            onChange={handleDateFromChange}
                            InputLabelProps={{
                                shrink: true,
                            }}
                            style={{ marginLeft: 10 }}
                        />
                    </div>
                    <div style={{ width: '100%' }}>
                        <TextField
                            label="Fecha Hasta"
                            type="datetime-local"
                            value={props.dateTo}
                            onChange={handleDateToChange}
                            InputLabelProps={{
                                shrink: true,
                            }}
                            style={{ marginLeft: 10 }}
                        />
                    </div>
                    <div style={{ width: '100%' }}>
                        <Typography variant="caption">
                            Seleccione tipo
                        </Typography>
                        <Select
                            fullWidth={true}
                            title="Tipo"
                            value={props.selectedType}
                            onChange={handleTypeChange}
                        >
                            {RACE_TYPES.map((type) => (
                                <MenuItem key={type.id} value={type.id}>
                                    {type.valor}
                                </MenuItem>
                            ))}
                        </Select>
                    </div>
                    <div style={{ width: '100%' }}>
                        <Typography variant="caption">
                            Seleccione estado
                        </Typography>
                        <Select
                            fullWidth={true}
                            title="Estado"
                            value={props.selectedState}
                            onChange={handleStateChange}
                        >
                            {RACE_STATES.map((type) => (
                                <MenuItem key={type.id} value={type.id}>
                                    {type.valor}
                                </MenuItem>
                            ))}
                        </Select>
                    </div>
                    {dataDrivers && dataDrivers.length > 0 && (
                        <div style={{ width: '100%' }}>
                            <Typography variant="caption">
                                Seleccione Conductor
                            </Typography>
                            <Select
                                fullWidth={true}
                                title="Conductor"
                                value={props.driverId}
                                onChange={handleDriverChange}
                            >
                                {dataDrivers.map((driver: any) => (
                                    <MenuItem key={driver.id} value={driver.id}>
                                        {driver.user.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </div>
                    )}
                    {dataBranches && dataBranches.length > 0 && (
                        <div style={{ width: '100%' }}>
                            <Typography variant="caption">
                                Seleccione sucursal
                            </Typography>
                            <Select
                                fullWidth={true}
                                title="Sucursal"
                                value={props.branchId}
                                onChange={handleBranchChange}
                            >
                                {dataBranches.map((branch: any) => (
                                    <MenuItem key={branch.id} value={branch.id}>
                                        {branch.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </div>
                    )}
                    {dataBranchesDestiny && dataBranchesDestiny.length > 0 && (
                        <div style={{ width: '100%' }}>
                            <Typography variant="caption">
                                Seleccione destino
                            </Typography>
                            <Select
                                fullWidth={true}
                                title="Destino"
                                value={props.branchDestinyId}
                                onChange={handleBranchDestinyChange}
                            >
                                {dataBranchesDestiny.map((branch: any) => (
                                    <MenuItem key={branch.id} value={branch.id}>
                                        {branch.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </div>
                    )}

                    <IconButton onClick={props.handleClearFilters} aria-label="clear filters" style={{ marginLeft: 10 }}>
                        <ClearIcon />
                    </IconButton>
                </div>
            )}
            <div style={{ width: '100%', margin: '0 auto', overflowX: 'auto' }}>
                <Table >
                    <TableHead>
                        <TableRow>
                            {showNormalColumns && (
                                <>
                                    <TableCell>
                                        <p style={{ margin: 0 }}>Acciones</p>
                                    </TableCell>
                                    <TableCell>Id</TableCell>
                                    <TableCell>Conductor</TableCell>
                                    <TableCell>Tipo</TableCell>
                                </>
                            )}
                            <TableCell>Cliente</TableCell>
                            <TableCell>Empresa</TableCell>
                            <TableCell>Sucursal Origen</TableCell>
                            {showNormalColumns && <TableCell>Punto</TableCell>}
                            <TableCell>Inicio</TableCell>
                            {showNormalColumns && (
                                <>
                                    <TableCell>Retiro de local</TableCell>
                                    <TableCell>Fin</TableCell>
                                    <TableCell>Estado</TableCell>
                                </>
                            )}
                            {racetoAdd === false && <TableCell>Distancia</TableCell>}
                            {showNormalColumns && (
                                <>
                                    <TableCell>Agregar documento</TableCell>
                                    <TableCell>Cambiar Estado</TableCell>
                                </>
                            )}
                            {racetoAdd && <TableCell>Seleccionar</TableCell>}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {data ? (
                            data.length > 0 ? (
                                data.map((race: any) => (
                                    <TableRow key={race.id}>
                                        {showNormalColumns && (
                                            <>
                                                <TableCell>
                                                    <Link to={"/race/" + race.id}>
                                                        <IconButton aria-label='Desabilitar' title='Ver'>
                                                            <HighlightOff />
                                                        </IconButton>
                                                    </Link>
                                                </TableCell>
                                                <TableCell>{race.id}</TableCell>
                                                <TableCell>
                                                    {race.driver && race.driver.user ? (
                                                        <Button onClick={() => props.setDialogDriver(true, race.driver)}>
                                                            {race.driver.user.name}
                                                        </Button>
                                                    ) : ''}
                                                </TableCell>
                                                <TableCell>
                                                    {RaceTypeLabels[race.type as keyof typeof RaceTypeLabels]}
                                                </TableCell>
                                            </>
                                        )}
                                        <TableCell>
                                            {race.owner && race.owner.user && race.owner.user.name ? (
                                                <Button onClick={() => props.setDialogClient(true, race.owner)}>
                                                    {race.owner.user.name}
                                                </Button>
                                            ) : ''}
                                        </TableCell>
                                        <TableCell>
                                            {race.branch && race.branch.company && race.branch.company.name ? race.branch.company.name : ''}
                                        </TableCell>
                                        <TableCell>
                                            {race.branchOrigin && race.branchOrigin.name ? (
                                                <Button onClick={() => props.setDialogBranch(true, race.branchOrigin)}>
                                                    {race.branchOrigin.name}
                                                </Button>
                                            ) : race.branch && race.branch.name ? (
                                                <Button onClick={() => props.setDialogBranch(true, race.branch)}>
                                                    {race.branch.name}
                                                </Button>
                                            ) : race.branch && race.branch.name ? race.branch.name : ''}
                                        </TableCell>
                                        {showNormalColumns && (
                                            <TableCell>
                                                {race.branchDestiny ? (
                                                    <Button onClick={() => props.setDialogBranch(true, race.branchDestiny)}>
                                                        {race.branchDestiny.name}
                                                    </Button>
                                                ) : ''}
                                            </TableCell>
                                        )}
                                        <TableCell>{race.startDate}</TableCell>
                                        {showNormalColumns && (
                                            <>
                                                <TableCell>{race.pickDate}</TableCell>
                                                <TableCell>
                                                    {race.endDate ? race.endDate : race.finishedTime ? race.finishedTime : ''}
                                                </TableCell>
                                                <TableCell>{getStateChip(race.state)}</TableCell>
                                            </>
                                        )}
                                        {racetoAdd === false && (
                                            <TableCell>
                                                (<span>{formatDistance(race.route.length)}</span>)
                                            </TableCell>
                                        )}
                                        {showNormalColumns && (
                                            <>
                                                <TableCell>
                                                    {!race.tipoComprobante && !race.nroComprobante && (
                                                        <IconButton
                                                            aria-label="Ver documento"
                                                            title="Ver documento"
                                                            onClick={() => props.setDialog(true, race)}
                                                        >
                                                            <AssignmentIcon />
                                                        </IconButton>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <IconButton onClick={() => props.setDialogChangeState(true, race)}>
                                                        <UpdateIcon />
                                                    </IconButton>
                                                </TableCell>
                                            </>
                                        )}
                                        {racetoAdd && props.setSelection && (
                                            <TableCell>
                                                <IconButton
                                                    aria-label="Seleccionar"
                                                    title="Seleccionar reparto"
                                                    onClick={() => props.setSelection && props.setSelection(race)}
                                                >
                                                    <CheckIcon />
                                                </IconButton>
                                            </TableCell>
                                        )}
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={3}>Sin resultados</TableCell>
                                </TableRow>
                            )) : (
                                <TableRow>
                                    <TableCell colSpan={20} style={{ textAlign: 'center' }}>
                                        <CircularProgress />
                                    </TableCell>
                                </TableRow>
                            )}
                    </TableBody>
                    <TableFooter>
                        <TableRow>
                            <TablePagination
                                count={data ? props.totalItems : 0}
                                page={props.pageNum}
                                rowsPerPage={props.pageSize}
                                onChangePage={handlePageChange}
                                onChangeRowsPerPage={handleRowsPerPageChange}
                            />
                        </TableRow>
                    </TableFooter>
                </Table>
            </div>
        </>
    );
};


// WEBPACK FOOTER //
// ./src/components/TableRacesComponent.tsx